import {
  diaNegocio,
  enlaceWhatsApp,
  formatearMoneda,
  formatearTelefono,
  hora12,
  type ClienteVista,
  type CompraResumen,
  type EstadoCita as Estado,
  type FichaCliente,
} from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ContactRound, MessageCircle, Pencil, Search, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, ErrorApi, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Pestana, PestanaContenido, Pestanas, PestanasLista, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { Campo, Input, Textarea } from '@/components/ui/form';
import { Dialogo, PanelLateral } from '@/components/ui/overlay';
import { diaLargo, fecha, fechaHora } from '@/lib/utils';
import { EstadoCita } from '../agenda/EstadoCita';
import { SelectOrigen } from './SelectOrigen';
import { useOrigenes } from './useOrigenes';
import { DetalleVenta, useVentaDetalle } from '../ventas/DetalleVenta';

function DialogoCliente({ cliente, onCerrar }: { cliente?: ClienteVista; onCerrar: () => void }) {
  const qc = useQueryClient();
  const { activos } = useOrigenes();
  const [d, setD] = useState({
    nombre: cliente?.nombre ?? '',
    telefono: cliente ? formatearTelefono(cliente.telefono) : '',
    fechaNacimiento: cliente?.fechaNacimiento ?? '',
    origen: cliente?.origen ?? '',
    origenDetalle: cliente?.origenDetalle ?? '',
    notas: cliente?.notas ?? '',
  });
  const guardar = useMutation({
    mutationFn: () => {
      const body = {
        nombre: d.nombre,
        telefono: d.telefono,
        fechaNacimiento: d.fechaNacimiento || null,
        origen: d.origen,
        origenDetalle: d.origenDetalle || null,
        notas: d.notas || null,
      };
      return cliente ? api(`/clientes/${cliente.id}`, { method: 'PATCH', body }) : api('/clientes', { method: 'POST', body });
    },
    onSuccess: () => {
      toast.success(cliente ? 'Cliente actualizado' : 'Cliente registrado');
      void qc.invalidateQueries({ queryKey: ['clientes'] });
      onCerrar();
    },
  });
  const err =
    guardar.error instanceof ErrorApi && Array.isArray(guardar.error.details)
      ? Object.fromEntries((guardar.error.details as { path: string[]; message: string }[]).map((x) => [x.path[0], x.message]))
      : {};
  const primerOrigen = activos[0]?.clave;
  useEffect(() => {
    if (!d.origen && primerOrigen) setD((v) => ({ ...v, origen: primerOrigen }));
  }, [d.origen, primerOrigen]);
  const set = (k: keyof typeof d) => (e: { target: { value: string } }) => setD((v) => ({ ...v, [k]: e.target.value }));

  return (
    <Dialogo
      abierto
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={cliente ? `Editar a ${cliente.nombre}` : 'Nuevo cliente'}
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} cargando={guardar.isPending} disabled={!d.nombre || !d.telefono || !d.origen}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo etiqueta="Nombre completo" requerido error={err.nombre} className="sm:col-span-2">
          {(p) => <Input {...p} value={d.nombre} onChange={set('nombre')} autoFocus />}
        </Campo>
        <Campo etiqueta="Teléfono (WhatsApp)" requerido error={err.telefono} ayuda="Si cambió de número, corrígelo aquí.">
          {(p) => <Input {...p} type="tel" value={d.telefono} onChange={set('telefono')} />}
        </Campo>
        <Campo etiqueta="Fecha de nacimiento" ayuda="Opcional; para el descuento de cumpleaños.">
          {(p) => <Input {...p} type="date" value={d.fechaNacimiento} onChange={set('fechaNacimiento')} />}
        </Campo>
        <Campo etiqueta="¿Cómo nos encontró?">
          {(p) => (
            <SelectOrigen {...p} valor={d.origen} actual={cliente?.origen} onCambiar={(origen) => setD((v) => ({ ...v, origen }))} />
          )}
        </Campo>
        <Campo etiqueta="Detalle del origen">
          {(p) => <Input {...p} value={d.origenDetalle} onChange={set('origenDetalle')} />}
        </Campo>
        <Campo etiqueta="Notas" className="sm:col-span-2">
          {(p) => <Textarea {...p} value={d.notas} onChange={set('notas')} maxLength={1000} />}
        </Campo>
        {guardar.error && !Object.keys(err).length && (
          <p role="alert" className="text-[13px] text-destructive sm:col-span-2">
            {mensajeError(guardar.error)}
          </p>
        )}
      </div>
    </Dialogo>
  );
}

/** Renglón de Clientes > Compras; con `ventas.ver_detalle` se despliega con el carrito completo. */
function CompraCliente({ compra: v, abierta, onAlternar }: { compra: CompraResumen; abierta: boolean; onAlternar: () => void }) {
  const { puede } = useSesion();
  const verDetalle = puede('ventas.ver_detalle');
  const detalle = useVentaDetalle(verDetalle && abierta ? v.id : null);
  const resumen = (
    <>
      <div className="flex items-center justify-between">
        <span className="text-[13px]">
          Folio {v.folio} · {fecha(v.fecha)}
        </span>
        <span className="cifras text-[13px] font-medium">{formatearMoneda(v.total)}</span>
      </div>
      <p className="text-[12.5px] text-foreground-muted">{v.conceptos}</p>
      {v.estado === 'cancelada' && (
        <Insignia tono="peligro" className="mt-1">
          Cancelada{v.motivoCancelacion ? `: ${v.motivoCancelacion}` : ''}
        </Insignia>
      )}
    </>
  );
  if (!verDetalle) return <li className="rounded-md border border-border px-3 py-2">{resumen}</li>;
  return (
    <li className="rounded-md border border-border">
      <button type="button" onClick={onAlternar} aria-expanded={abierta} className="w-full rounded-md px-3 py-2 text-left hover:bg-muted">
        {resumen}
      </button>
      {abierta && (
        <div className="border-t border-border px-3 py-3">
          {detalle.data ? <DetalleVenta venta={detalle.data} /> : detalle.isError ? <p className="text-[13px] text-destructive">{mensajeError(detalle.error)}</p> : <Esqueleto className="h-32" />}
        </div>
      )}
    </li>
  );
}

function FichaClientePanel({ id, onCerrar, onEditar }: { id: string; onCerrar: () => void; onEditar: (c: ClienteVista) => void }) {
  const { etiqueta } = useOrigenes();
  const ficha = useQuery({ queryKey: ['clientes', 'ficha', id], queryFn: () => api<FichaCliente>(`/clientes/${id}/ficha`) });
  const f = ficha.data;
  const [abierta, setAbierta] = useState<string | null>(null);
  return (
    <PanelLateral
      abierto
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={f?.nombre ?? 'Cliente'}
      descripcion={f ? `Cliente desde ${etiqueta(f.origen)}` : undefined}
      pie={
        f && (
          <Button variante="secundario" onClick={() => onEditar(f)}>
            <Pencil /> Editar datos
          </Button>
        )
      }
    >
      {!f ? (
        <Esqueleto className="h-64" />
      ) : (
        <div className="flex flex-col gap-5">
          <a href={enlaceWhatsApp(f.telefono)} target="_blank" rel="noopener noreferrer" className="cifras inline-flex items-center gap-1.5 text-accent hover:underline">
            <MessageCircle className="size-4" aria-hidden /> {formatearTelefono(f.telefono)}
          </a>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              ['Visitas', f.visitas],
              ['Cancelaciones', f.cancelaciones],
              ['Retardos', f.retardos],
              ['No vino', f.noShows],
              ['Citas', f.citasTotal],
              ...(f.totalGastado !== undefined ? [['Gastado', formatearMoneda(f.totalGastado)]] : []),
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-surface-alt px-2 py-2">
                <p className="cifras font-display text-[18px] leading-tight">{v}</p>
                <p className="text-[11px] text-foreground-muted">{k}</p>
              </div>
            ))}
          </div>
          <dl className="grid gap-2 text-[13.5px]">
            <div className="flex justify-between gap-3">
              <dt className="text-foreground-muted">Última visita</dt>
              <dd>{f.ultimaVisita ? fecha(f.ultimaVisita) : '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-foreground-muted">Siguiente reserva</dt>
              <dd className="text-right">
                {f.siguienteReserva
                  ? `${diaLargo(diaNegocio(new Date(f.siguienteReserva.inicio)))} ${hora12(new Date(f.siguienteReserva.inicio))}`
                  : '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-foreground-muted">Barbero que frecuenta</dt>
              <dd>{f.barberoFrecuente?.nombre ?? '—'}</dd>
            </div>
          </dl>

          <Pestanas defaultValue="citas">
            <PestanasLista>
              <Pestana value="citas">Reservas</Pestana>
              <Pestana value="compras">Compras</Pestana>
            </PestanasLista>
            <PestanaContenido value="citas" className="pt-3">
              <ul className="flex flex-col gap-2">
                {f.historialCitas.length === 0 && <li className="text-[13px] text-foreground-muted">Sin reservas</li>}
                {f.historialCitas.map((c) => (
                  <li key={c.id} className="rounded-md border border-border px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="cifras text-[13px]">{fechaHora(c.inicio)}</span>
                      <EstadoCita estado={c.estado as Estado} />
                    </div>
                    <p className="text-[12.5px] text-foreground-muted">
                      {c.servicio}
                      {c.barbero ? ` · ${c.barbero}` : ''}
                      {c.llegoTarde ? ' · llegó tarde' : ''}
                    </p>
                    {c.motivoCancelacion && <p className="text-[12.5px]">Motivo: {c.motivoCancelacion}</p>}
                  </li>
                ))}
              </ul>
            </PestanaContenido>
            <PestanaContenido value="compras" className="pt-3">
              <ul className="flex flex-col gap-2">
                {f.compras.length === 0 && <li className="text-[13px] text-foreground-muted">Sin compras</li>}
                {f.compras.map((v) => (
                  <CompraCliente key={v.id} compra={v} abierta={abierta === v.id} onAlternar={() => setAbierta(abierta === v.id ? null : v.id)} />
                ))}
              </ul>
            </PestanaContenido>
          </Pestanas>
        </div>
      )}
    </PanelLateral>
  );
}

export function ClientesPage() {
  const { id } = useSearch({ strict: false }) as { id?: string };
  const navegar = useNavigate();
  const { puede } = useSesion();
  const [busqueda, setBusqueda] = useState('');
  const [diferida, setDiferida] = useState('');
  const [editar, setEditar] = useState<ClienteVista | 'nuevo' | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setDiferida(busqueda.trim()), 250);
    return () => clearTimeout(t);
  }, [busqueda]);
  const clientes = useQuery({ queryKey: ['clientes', 'lista', diferida], queryFn: () => api<ClienteVista[]>('/clientes', { query: { q: diferida } }) });

  return (
    <ModuleLayout titulo="Clientes">
      <EncabezadoPagina
        titulo="Clientes"
        descripcion="Directorio con historial de reservas, compras, cancelaciones y retardos."
        acciones={
          puede('clientes.editar') && (
            <Button onClick={() => setEditar('nuevo')}>
              <UserPlus /> Nuevo cliente
            </Button>
          )
        }
      />
      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground-muted" aria-hidden />
        <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar por nombre o teléfono…" className="h-10 pl-9" aria-label="Buscar cliente" />
      </div>
      <Tarjeta>
        {clientes.isLoading ? (
          <div className="p-4">
            <Esqueleto className="h-40" />
          </div>
        ) : !clientes.data?.length ? (
          <Vacio icono={<ContactRound />} titulo={diferida ? 'Sin coincidencias' : 'Aún no hay clientes'} descripcion="Los clientes se registran al agendar su primera cita." />
        ) : (
          <Tabla>
            <thead>
              <tr>
                <Th>Nombre</Th>
                <Th>Teléfono</Th>
                <Th className="text-right">Citas</Th>
                <Th className="text-right">Cancelaciones</Th>
                <Th className="text-right">Retardos</Th>
                <Th>Última visita</Th>
              </tr>
            </thead>
            <tbody>
              {clientes.data.map((c) => (
                <Tr key={c.id} className="cursor-pointer" onClick={() => void navegar({ to: '/clientes', search: { id: c.id } })}>
                  <Td className="font-medium">{c.nombre}</Td>
                  <Td className="cifras text-foreground-muted">{formatearTelefono(c.telefono)}</Td>
                  <Td className="cifras text-right">{c.citasTotal}</Td>
                  <Td className="cifras text-right">{c.cancelaciones}</Td>
                  <Td className="cifras text-right">{c.retardos}</Td>
                  <Td className="text-foreground-muted">{c.ultimaVisita ? fecha(c.ultimaVisita) : '—'}</Td>
                </Tr>
              ))}
            </tbody>
          </Tabla>
        )}
      </Tarjeta>
      {id && <FichaClientePanel id={id} onCerrar={() => void navegar({ to: '/clientes', search: {} })} onEditar={(c) => setEditar(c)} />}
      {editar && <DialogoCliente cliente={editar === 'nuevo' ? undefined : editar} onCerrar={() => setEditar(null)} />}
    </ModuleLayout>
  );
}
